import type { DistanceMeasurementMouseOverEvent, PointerLens, Viewer } from '@xeokit/xeokit-sdk';

export const useViewerDistanceMeasurementsStore = defineStore(
  'viewer-distance-measurements-store',
  () => {
    const {
      $ContextMenu,
      $DistanceMeasurementsPlugin,
      $DistanceMeasurementsMouseControl,
      $DistanceMeasurementsTouchControl,
      $DistanceMeasurementEditMouseControl,
      $DistanceMeasurementEditTouchControl,
      $os,
    } = useNuxtApp();
    const { t, locale } = useI18n();

    const isInitialized = ref(false);
    const measurementEditEnabled = ref(false);
    const measurementMouseOver = ref(false);

    let plugin: InstanceType<typeof $DistanceMeasurementsPlugin> | undefined;

    let mouseControl: InstanceType<typeof $DistanceMeasurementsMouseControl> | undefined;
    let touchControl: InstanceType<typeof $DistanceMeasurementsTouchControl> | undefined;

    let editMouseControl: InstanceType<typeof $DistanceMeasurementEditMouseControl> | undefined;
    let editTouchControl: InstanceType<typeof $DistanceMeasurementEditTouchControl> | undefined;

    let contextMenu: InstanceType<typeof $ContextMenu>;

    watch(locale, () => {
      contextMenu = returnNewContextMenu();
    });

    function getPlugin() {
      return plugin;
    }

    function getMouseControl() {
      return mouseControl;
    }

    function getTouchControl() {
      return touchControl;
    }

    function getEditMouseControl() {
      return editMouseControl;
    }

    function getEditTouchControl() {
      return editTouchControl;
    }

    function getContextMenu() {
      return contextMenu;
    }

    function init() {
      const viewerStore = useViewerStore();
      const viewer = viewerStore.getHelpers().viewer as Viewer;

      if (!viewer) return;

      plugin = new $DistanceMeasurementsPlugin(viewer);

      mouseControl = new $DistanceMeasurementsMouseControl(plugin, {
        pointerLens: viewerStore.getHelpers().pointerLens as PointerLens | undefined,
        snapping: true,
      });

      touchControl = new $DistanceMeasurementsTouchControl(plugin, {
        pointerLens: viewerStore.getHelpers().pointerLens as PointerLens | undefined,
        // @ts-ignore xeokit
        snapToVertex: true,
        snapToEdge: true,
      });

      //plugin.on('measurementCreated', onMeasurementCreated);
      //plugin.on('measurementCancel', onMeasurementCancel);
      plugin.on('mouseOver', onMouseOver);
      plugin.on('mouseLeave', onMouseLeave);
      plugin.on('contextMenu', onContextMenu);

      contextMenu = returnNewContextMenu();
      contextMenu.on('hidden', onContextMenuHidden);

      isInitialized.value = true;
    }

    function onMouseOver(event: DistanceMeasurementMouseOverEvent) {
      measurementMouseOver.value = true;
      if (measurementEditEnabled.value) return;
      (event.measurement as any).setHighlighted(true);
    }

    function onMouseLeave(event: DistanceMeasurementMouseOverEvent) {
      measurementMouseOver.value = false;
      if (measurementEditEnabled.value) return;
      if (contextMenu.shown && contextMenu.context.distanceMeasurement.id === event.measurement.id)
        return;
      (event.measurement as any).setHighlighted(false);
    }

    function onContextMenu(e: any) {
      const viewerStore = useViewerStore();
      const viewer = viewerStore.getHelpers().viewer as Viewer;

      if (!viewer) return;

      const plugin = getPlugin();
      if (!plugin) return;

      contextMenu.context = {
        viewer: viewer,
        distanceMeasurementsPlugin: plugin,
        distanceMeasurement: e.distanceMeasurement,
      };

      contextMenu.show(e.event.clientX, e.event.clientY);

      e.event.preventDefault();
    }

    function onContextMenuHidden() {
      if (contextMenu.context.distanceMeasurement) {
        contextMenu.context.distanceMeasurement.setHighlighted(false);
      }
    }

    function returnNewContextMenu() {
      let endMeasurementEdit: any = null;

      return new $ContextMenu({
        items: [
          [
            {
              getTitle: () => t('components.xeokit_viewer.context_menu.delete') || 'Delete',
              doAction: function (context) {
                console.log({ context });
                context.distanceMeasurement.destroy();
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.axisVisible
                  ? t('components.xeokit_viewer.context_menu.hide_axis') || 'Hide Axis'
                  : t('components.xeokit_viewer.context_menu.show_axis') || 'Show Axis';
              },
              doAction: function (context) {
                context.distanceMeasurement.axisVisible = !context.distanceMeasurement.axisVisible;
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.xLabelEnabled &&
                  context.distanceMeasurement.labelsVisible
                  ? t('components.xeokit_viewer.context_menu.disable_x_label') || 'Disable X Label'
                  : t('components.xeokit_viewer.context_menu.enable_x_label') || 'Enable X Label';
              },
              doAction: function (context) {
                context.distanceMeasurement.xLabelEnabled =
                  !context.distanceMeasurement.xLabelEnabled;
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.yLabelEnabled &&
                  context.distanceMeasurement.labelsVisible
                  ? t('components.xeokit_viewer.context_menu.disable_y_label') || 'Disable Y Label'
                  : t('components.xeokit_viewer.context_menu.enable_y_label') || 'Enable Y Label';
              },
              doAction: function (context) {
                context.distanceMeasurement.yLabelEnabled =
                  !context.distanceMeasurement.yLabelEnabled;
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.zLabelEnabled &&
                  context.distanceMeasurement.labelsVisible
                  ? t('components.xeokit_viewer.context_menu.disable_z_label') || 'Disable Z Label'
                  : t('components.xeokit_viewer.context_menu.enable_z_label') || 'Enable Z Label';
              },
              doAction: function (context) {
                context.distanceMeasurement.zLabelEnabled =
                  !context.distanceMeasurement.zLabelEnabled;
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.lengthLabelEnabled &&
                  context.distanceMeasurement.labelsVisible
                  ? t('components.xeokit_viewer.context_menu.disable_length_label') ||
                      'Disable Length Label'
                  : t('components.xeokit_viewer.context_menu.enable_length_label') ||
                      'Enable Length Label';
              },
              doAction: function (context) {
                context.distanceMeasurement.lengthLabelEnabled =
                  !context.distanceMeasurement.lengthLabelEnabled;
              },
            },
            {
              getTitle: (context) => {
                return context.distanceMeasurement.labelsVisible
                  ? t('components.xeokit_viewer.context_menu.hide_all_labels') || 'Hide All Labels'
                  : t('components.xeokit_viewer.context_menu.show_all_labels') || 'Show All Labels';
              },
              getEnabled: () => {
                return getNumMeasurements() > 0;
              },
              doAction: function (context) {
                context.distanceMeasurement.labelsVisible =
                  !context.distanceMeasurement.labelsVisible;
              },
            },
            {
              getTitle: () => t('components.xeokit_viewer.context_menu.edit') || 'Edit',
              getEnabled: function () {
                return !measurementEditEnabled.value;
              },
              doAction: function (context) {
                const viewerStore = useViewerStore();
                const measurement = context.distanceMeasurement;

                const viewer = viewerStore.getHelpers().viewer;

                if (!viewer) return;

                //@ts-ignore
                const viewerCanvas = viewer.scene.canvas.canvas as HTMLCanvasElement;

                if ($os.isTouchDevice()) {
                  if (!touchControl) return;
                  touchControl.deactivate();

                  editTouchControl = new $DistanceMeasurementEditTouchControl(measurement, {
                    pointerLens: viewerStore.getHelpers().pointerLens,
                    snapping: true,
                  });

                  editTouchControl.on('edited', () => {
                    console.log('edited');
                  });

                  endMeasurementEdit = () => {
                    editTouchControl?.deactivate();
                    endMeasurementEdit = null;
                  };
                }

                const handleMouseControl = () => {
                  if (!mouseControl) return;
                  mouseControl.deactivate();

                  editMouseControl = new $DistanceMeasurementEditMouseControl(measurement, {
                    pointerLens: viewerStore.getHelpers().pointerLens,
                    snapping: true,
                  });

                  viewerCanvas.removeEventListener('mousemove', handleMouseControl);
                };

                viewerCanvas.addEventListener('mousemove', handleMouseControl);

                endMeasurementEdit = () => {
                  editMouseControl && editMouseControl.deactivate();
                  mouseControl && mouseControl.activate();
                  editTouchControl && editTouchControl.deactivate();
                  touchControl && touchControl.activate();
                  endMeasurementEdit = null;
                };

                measurementEditEnabled.value = true;
              },
            },
            {
              getTitle: () =>
                t('components.xeokit_viewer.context_menu.end_editing') || 'End Editing',
              getEnabled: function () {
                return measurementEditEnabled.value;
              },

              doAction: function () {
                measurementEditEnabled.value = false;

                endMeasurementEdit();
                if (editMouseControl) editMouseControl.deactivate();
                if (editTouchControl) editTouchControl.deactivate();
              },
            },
          ],
          [
            {
              getTitle: () => t('components.xeokit_viewer.context_menu.delete_all') || 'Delete All',
              getEnabled: function (context) {
                return Object.keys(context.distanceMeasurementsPlugin.measurements).length > 0;
              },
              doAction: function (context) {
                context.distanceMeasurementsPlugin.clear();
              },
            },
            {
              getTitle: () =>
                t('components.xeokit_viewer.context_menu.cancel_measurement') ||
                'Cancel Measurement',
              doAction: function () {
                mouseControl?.reset();
              },
            },
          ],
        ],
      });
    }

    function getMeasurementsSnappingEnabled() {
      const mouseControl = getMouseControl();
      if (!mouseControl) return;

      return mouseControl.snapping;
    }

    function setMeasurementsSnappingEnabled(state: boolean) {
      const mouseControl = getMouseControl();
      if (!mouseControl) return;

      mouseControl.snapping = state;
    }

    function getNumMeasurements(): number {
      const plugin = getPlugin();
      if (!plugin) return 0;

      return Object.keys(plugin.measurements).length;
    }

    function setMeasurementsVisible(visible: boolean) {
      const plugin = getPlugin();
      if (!plugin) return;

      Object.values(plugin.measurements).forEach((measurement) => {
        measurement.visible = visible;
      });
    }

    function getMeasurementsAxisVisible(): boolean {
      const plugin = getPlugin();
      if (!plugin) return false;

      return plugin.getAxisVisible();
    }

    function setMeasurementsAxisVisible(axisVisible: boolean) {
      const plugin = getPlugin();
      if (!plugin) return;

      // @ts-ignore
      plugin.setAxisVisible(axisVisible);
    }

    function clearMeasurements() {
      const plugin = getPlugin();
      if (!plugin) return;

      plugin.clear();
    }

    function setLabelsShown(shown: boolean) {
      const plugin = getPlugin();
      if (!plugin) return;

      // @ts-ignore xeokit
      //plugin.setLabelsShown(shown);

      Object.values(plugin.measurements).forEach((measurement) => {
        measurement.labelsVisible = shown;
      });
    }

    return {
      measurementMouseOver,
      measurementEditEnabled,
      isInitialized,
      getPlugin,
      getMouseControl,
      getTouchControl,
      getEditMouseControl,
      getEditTouchControl,
      getContextMenu,
      init,
      getNumMeasurements,
      getMeasurementsSnappingEnabled,
      setMeasurementsSnappingEnabled,
      getMeasurementsAxisVisible,
      setMeasurementsAxisVisible,
      setMeasurementsVisible,
      clearMeasurements,
      setLabelsShown,
    };
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useViewerDistanceMeasurementsStore, import.meta.hot));
}
